import React, { useState, useEffect } from 'react'
import * as FileSaver from 'file-saver'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import SvgIcon from '@material-ui/core/SvgIcon'
import IconButton from '@material-ui/core/IconButton'

import { useAuth0 } from '@/auth/index'
import { getConfig } from '@/config/runtime'
import { downloadProjectFile } from '@/shared/services/prodigy-file'
import { DefaultFileIcon, FileIcons } from '@/shared/constants/file-icons'
import { downloadFromUrl } from '@/utils/download-file'

const FILESTACK_CDN_REGEX = /^https?:\/\/(?:[^/]+\.)?filestackcontent\.com\//i

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  fileItem: {
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  iconButtonWrapper: {
    maxWidth: 23,
    minWidth: 23,
    width: 23,
  },
  iconButton: {
    width: 12,
    padding: 0,
  },
}))

const ProjectItemFilesComponent = (props: any) => {
  const { name, InputProps, handleCancel, handleChange } = props

  const classes = useStyles()
  const { getIdTokenClaims } = useAuth0()
  const [tempFiles, setTempFiles] = useState([] as any)

  useEffect(() => {
    const files = InputProps.value || []
    setTempFiles(files)

    return () => {}
  }, [InputProps.value])

  const handleDeleteFile = (index: number) => {
    const temp = tempFiles.filter((t: any, i: number) => i !== index)
    setTempFiles(temp)
    handleChange(temp)
  }

  const onDownloadPDF = async (tempFile: any) => {
    const filename = tempFile.filename || 'project_attachment'
    const filesMigrated = getConfig().FILES_MIGRATED

    if (!filesMigrated && tempFile.url && FILESTACK_CDN_REGEX.test(tempFile.url)) {
      downloadFromUrl(tempFile.url, filename)
      return
    }

    try {
      const claims = await getIdTokenClaims()
      const token = (claims as any)?.__raw
      const { downloadUrl } = await downloadProjectFile(tempFile.key || tempFile.filename, { token })
      await downloadFromUrl(downloadUrl, filename)
    } catch (err) {
      console.error('Download failed', err)
    }
  }

  return (
    <Grid container spacing={2}>
      {tempFiles.map((tempFile: any, i: number) => (
        <Grid key={`project-file-${i}`} item xs={12} className={classes.fileItem}>
          <Grid container spacing={1}>
            <Grid item xs className={'pag-text-ellipsis'}>
              <FontAwesomeIcon icon={FileIcons[tempFile.mimetype] || DefaultFileIcon} />
              &nbsp;&nbsp;
              {tempFile.filename || ''}
            </Grid>

            <Grid item xs className={classes.iconButtonWrapper}>
              <Tooltip title="Delete">
                <IconButton
                  aria-label="delete"
                  color="inherit"
                  disableFocusRipple
                  size="small"
                  className={classes.iconButton}
                  onClick={() => handleDeleteFile(i)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 22 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.14041 0.300049C7.14562 0.300049 6.32041 1.12526 6.32041 2.12005V2.90005H1.38041C1.35335 2.89864 1.32623 2.89864 1.29916 2.90005C1.19672 2.90537 1.09633 2.93081 1.00372 2.97493C0.911109 3.01905 0.828098 3.08098 0.759427 3.15719C0.690755 3.23339 0.637768 3.32238 0.60349 3.41906C0.569212 3.51575 0.554315 3.61824 0.559651 3.72068C0.564987 3.82312 0.59045 3.92351 0.634586 4.01611C0.678723 4.10871 0.740668 4.19171 0.816884 4.26037C0.893101 4.32903 0.982096 4.382 1.07879 4.41626C1.17548 4.45052 1.27797 4.4654 1.38041 4.46005H18.0042L17.5004 21.8557C17.4951 22.0405 17.3977 22.14 17.2404 22.14H4.76041C4.60309 22.14 4.50577 22.0405 4.50041 21.8557L4.04541 6.2638C4.04276 6.16136 4.01995 6.06045 3.97829 5.96682C3.93663 5.8732 3.87694 5.7887 3.80262 5.71815C3.7283 5.64759 3.64082 5.59237 3.54516 5.55563C3.4495 5.51889 3.34753 5.50135 3.2451 5.50402C3.14266 5.50669 3.04175 5.52951 2.94813 5.57118C2.85451 5.61285 2.77002 5.67255 2.69947 5.74687C2.62893 5.8212 2.57371 5.90869 2.53699 6.00436C2.50026 6.10002 2.48273 6.20199 2.48541 6.30442L2.94041 21.8963C2.96844 22.8633 3.76541 23.7 4.76041 23.7H17.2404C18.2354 23.7 19.0324 22.8633 19.0604 21.8963L19.5642 4.46005H20.6204C20.7238 4.46151 20.8264 4.44242 20.9223 4.40387C21.0182 4.36533 21.1055 4.30811 21.1791 4.23553C21.2527 4.16296 21.3112 4.07648 21.3511 3.98112C21.391 3.88576 21.4115 3.78342 21.4115 3.68005C21.4115 3.57668 21.391 3.47434 21.3511 3.37898C21.3112 3.28362 21.2527 3.19714 21.1791 3.12456C21.1055 3.05199 21.0182 2.99477 20.9223 2.95622C20.8264 2.91768 20.7238 2.89859 20.6204 2.90005H15.6804V2.12005C15.6804 1.12526 14.8552 0.300049 13.8604 0.300049H8.14041ZM8.14041 1.86005H13.8604C14.0179 1.86005 14.1204 1.96252 14.1204 2.12005V2.90005H7.88041V2.12005C7.88041 1.96252 7.98288 1.86005 8.14041 1.86005ZM7.35229 7.0438C7.24916 7.04486 7.14727 7.06636 7.05251 7.10705C6.95775 7.14775 6.872 7.20683 6.80022 7.28088C6.72843 7.35492 6.67205 7.44247 6.63432 7.53845C6.59659 7.63443 6.57827 7.73694 6.58041 7.84005V18.76C6.57895 18.8634 6.59805 18.966 6.63659 19.0619C6.67513 19.1579 6.73235 19.2452 6.80493 19.3188C6.8775 19.3924 6.96398 19.4508 7.05934 19.4907C7.1547 19.5306 7.25704 19.5512 7.36041 19.5512C7.46378 19.5512 7.56612 19.5306 7.66148 19.4907C7.75684 19.4508 7.84332 19.3924 7.9159 19.3188C7.98847 19.2452 8.04569 19.1579 8.08423 19.0619C8.12278 18.966 8.14187 18.8634 8.14041 18.76V7.84005C8.14258 7.73557 8.12374 7.63173 8.08501 7.53467C8.04627 7.43762 7.98843 7.34934 7.91492 7.27507C7.84141 7.2008 7.75373 7.14206 7.65708 7.10233C7.56043 7.0626 7.45678 7.0427 7.35229 7.0438ZM10.9923 7.0438C10.8892 7.04486 10.7873 7.06636 10.6925 7.10705C10.5977 7.14775 10.512 7.20683 10.4402 7.28088C10.3684 7.35492 10.312 7.44247 10.2743 7.53845C10.2366 7.63443 10.2183 7.73694 10.2204 7.84005V18.76C10.2189 18.8634 10.238 18.966 10.2766 19.0619C10.3151 19.1579 10.3724 19.2452 10.4449 19.3188C10.5175 19.3924 10.604 19.4508 10.6993 19.4907C10.7947 19.5306 10.897 19.5512 11.0004 19.5512C11.1038 19.5512 11.2061 19.5306 11.3015 19.4907C11.3968 19.4508 11.4833 19.3924 11.5559 19.3188C11.6285 19.2452 11.6857 19.1579 11.7242 19.0619C11.7628 18.966 11.7819 18.8634 11.7804 18.76V7.84005C11.7826 7.73557 11.7637 7.63173 11.725 7.53467C11.6863 7.43762 11.6284 7.34934 11.5549 7.27507C11.4814 7.2008 11.3937 7.14206 11.2971 7.10233C11.2004 7.0626 11.0968 7.0427 10.9923 7.0438ZM14.6323 7.0438C14.5292 7.04486 14.4273 7.06636 14.3325 7.10705C14.2377 7.14775 14.152 7.20683 14.0802 7.28088C14.0084 7.35492 13.952 7.44247 13.9143 7.53845C13.8766 7.63443 13.8583 7.73694 13.8604 7.84005V18.76C13.8589 18.8634 13.878 18.966 13.9166 19.0619C13.9551 19.1579 14.0124 19.2452 14.0849 19.3188C14.1575 19.3924 14.244 19.4508 14.3393 19.4907C14.4347 19.5306 14.537 19.5512 14.6404 19.5512C14.7438 19.5512 14.8461 19.5306 14.9415 19.4907C15.0368 19.4508 15.1233 19.3924 15.1959 19.3188C15.2685 19.2452 15.3257 19.1579 15.3642 19.0619C15.4028 18.966 15.4219 18.8634 15.4204 18.76V7.84005C15.4226 7.73557 15.4037 7.63173 15.365 7.53467C15.3263 7.43762 15.2684 7.34934 15.1949 7.27507C15.1214 7.2008 15.0337 7.14206 14.9371 7.10233C14.8404 7.0626 14.7368 7.0427 14.6323 7.0438Z"
                      fill="#7B7B7B"
                    />
                  </svg>
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs className={classes.iconButtonWrapper} style={{ textAlign: 'right' }}>
              <Tooltip title="Download">
                <IconButton
                  aria-label="download"
                  color="inherit"
                  disableFocusRipple
                  size="small"
                  className={classes.iconButton}
                  onClick={() => onDownloadPDF(tempFile)}
                >
                  <SvgIcon style={{ fontSize: 16 }}>
                    <svg
                      width="18"
                      height="22"
                      viewBox="0 0 22 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.40334 11.8497C5.35498 11.7901 5.3245 11.718 5.31543 11.6418C5.30635 11.5656 5.31905 11.4883 5.35206 11.4191C5.38507 11.3498 5.43703 11.2912 5.50192 11.2503C5.56682 11.2093 5.642 11.1875 5.71875 11.1875H8.5625V1.03125C8.5625 0.923506 8.6053 0.820175 8.68149 0.743988C8.75767 0.667801 8.86101 0.625 8.96875 0.625H13.0312C13.139 0.625 13.2423 0.667801 13.3185 0.743988C13.3947 0.820175 13.4375 0.923506 13.4375 1.03125V11.1875H16.2812C16.358 11.1875 16.4332 11.2093 16.4981 11.2503C16.563 11.2912 16.6149 11.3498 16.6479 11.4191C16.6809 11.4883 16.6936 11.5656 16.6846 11.6418C16.6755 11.718 16.645 11.7901 16.5967 11.8497L11.3154 18.3497C11.2773 18.3966 11.2292 18.4344 11.1747 18.4604C11.1201 18.4864 11.0604 18.4999 11 18.4999C10.9396 18.4999 10.8799 18.4864 10.8253 18.4604C10.7708 18.4344 10.7227 18.3966 10.6846 18.3497L5.40334 11.8497ZM20.75 17.6875H18.3125C18.2048 17.6875 18.1014 17.7303 18.0252 17.8065C17.9491 17.8827 17.9062 17.986 17.9062 18.0938V20.125H4.09375V18.0938C4.09375 17.986 4.05095 17.8827 3.97476 17.8065C3.89858 17.7303 3.79524 17.6875 3.6875 17.6875H1.25C1.14226 17.6875 1.03892 17.7303 0.962738 17.8065C0.886551 17.8827 0.84375 17.986 0.84375 18.0938V22.9688C0.84375 23.0765 0.886551 23.1798 0.962738 23.256C1.03892 23.3322 1.14226 23.375 1.25 23.375H20.75C20.8577 23.375 20.9611 23.3322 21.0373 23.256C21.1134 23.1798 21.1562 23.0765 21.1562 22.9688V18.0938C21.1562 17.986 21.1134 17.8827 21.0373 17.8065C20.9611 17.7303 20.8577 17.6875 20.75 17.6875Z"
                        fill="#7B7B7B"
                      />
                    </svg>
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      ))}
      {(!tempFiles || !tempFiles.length) && (
        <Grid item xs={12}>
          <Typography color="inherit" variant="subtitle1" align="center">
            <b>You haven't added</b>
            <br />
            <b>any files yet.</b>
          </Typography>
          <Typography color="inherit" variant="subtitle2" align="center">
            Click the + sign to add your your first file.
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}

ProjectItemFilesComponent.defaultProps = {
  handleChange: (e: any) => {},
  handleCancel: (e: any) => {},
}

export default ProjectItemFilesComponent
